/* eslint-disable no-console */
import {ReloadOutlined} from '@ant-design/icons';
import {Button, Flex, Select} from 'antd';
import React, {useEffect, useRef, useState} from 'react';

import Loader from '../../../../components/Loader/Loader';
import {subscriptionPlanTypes} from '../../../../constants/subscriptionConstants';
import {useGlobalYear} from '../../../../hooks/useGlobalYear';
import {getIndustryBenchmarkOverview} from '../../../../services/modules/ClimateDB/industryBenchmarkAPI';
import {regenerateOverviewInsights} from '../../../../services/modules/ClimateDB/overviewInsightAPI';
import activityPerformance from '../../../../services/modules/DASHBOARD/activityPerformance';
import emissionCalculation from '../../../../services/modules/DASHBOARD/emissionCalculation';
import listOfActivity from '../../../../services/modules/DASHBOARD/listOfActivity';
import reduction from '../../../../services/modules/DASHBOARD/reduction';
import reductionTimeline from '../../../../services/modules/DASHBOARD/reductionTimeline';
import country from '../../../../services/modules/JSON/country';
import statesJSON from '../../../../services/modules/JSON/statesJSON';
import useCompanyStore from '../../../../store/companyStore/companyDetailsStore';
import usePlatformStore from '../../../../store/platformStore/platformStore';
import {yearSelect} from '../../../../utils/FormUtils/yearSelect';
import {
  getSubscriptionPlanType,
  shouldBlockReadOnly
} from '../../../../utils/SubscriptionHelper';

import ListOfActivities from './ListOfActivities';

// KPIs of one industry benchmark group (`emissions` | `intensity` | `consumption`).
// Each KPI already carries the `dataId`, `label`, `value`, `benchmark` and
// `unit` fields the benchmark cards read.
const industryBenchmarkGroupKpis = (
  industryBenchmark: any,
  key: string
): any[] | undefined =>
  industryBenchmark?.groups?.find((group: any) => group?.key === key)?.kpis;

const Metrics: React.FC = () => {
  const {selectedYear, setSelectedYear} = useGlobalYear();
  const setFacilitySelectedYear = usePlatformStore(
    (state) => state.setFacilitySelectedYear
  );
  const [isMetricsLoading, setIsMetricsLoading] = useState(true);
  const [isReductionLoading, setIsReductionLoading] = useState(true);
  const [activityData, setActivityData] = useState<any>([]);
  const [listData, setListData] = useState<any>([]);
  const [refresing, setRefresing] = useState<boolean>();
  const [refreshPage, setRefreshPage] = useState(false);
  const [industryBenchmarksData, setIndustryBenchmarksData] = useState<any>({});
  const isLoading = isMetricsLoading || isReductionLoading;
  const isFreePlan = getSubscriptionPlanType() === subscriptionPlanTypes.FREE;
  const silentRefresh = useRef<{page: boolean; year: string} | null>(null);

  useEffect(() => {
    if (selectedYear) {
      const marker = silentRefresh.current;
      const isButtonRefresh =
        marker?.page === refreshPage && marker.year === selectedYear;

      if (!isButtonRefresh) {
        setIsMetricsLoading(true);
      }
      Promise.all([
        activityPerformance(selectedYear),
        listOfActivity(selectedYear),
        getIndustryBenchmarkOverview(selectedYear)
      ])
        .then(([activity, list, industryBenchmark]) => {
          setActivityData(activity);
          setListData(list?.data);
          setIndustryBenchmarksData((prev: any) => ({
            ...prev,
            emissionsData: industryBenchmarkGroupKpis(
              industryBenchmark,
              'emissions'
            )?.map((item: any) => {
              return item.dataId === 'Scope 3 GHG emissions' && isFreePlan
                ? {
                    ...item,
                    value: 0
                  }
                : item;
            }),
            intensityData: industryBenchmarkGroupKpis(
              industryBenchmark,
              'intensity'
            ),
            consumptionData: industryBenchmarkGroupKpis(
              industryBenchmark,
              'consumption'
            )?.map((item: any) => {
              return item.dataId === 'Total quantity of waste generated' &&
                isFreePlan
                ? {
                    ...item,
                    value: 0
                  }
                : item;
            })
          }));
        })
        .catch(() => {
          setIndustryBenchmarksData((prev: any) => ({
            ...prev,
            emissionsData: undefined,
            intensityData: undefined,
            consumptionData: undefined
          }));
        })
        .finally(() => {
          setIsMetricsLoading(false);
          country().catch(() => {});
          statesJSON().catch(() => {});
        });
    }
  }, [selectedYear, refreshPage]);

  useEffect(() => {
    const isButtonRefresh = silentRefresh.current?.page === refreshPage;

    if (!isButtonRefresh) {
      setIsReductionLoading(true);
    }
    Promise.all([reduction(), reductionTimeline()])
      .then(([reductionRes, reductionTimelineRes]) => {
        setIndustryBenchmarksData((prev: any) => ({
          ...prev,
          reductionData: reductionRes?.data,
          reductionTimelineData: reductionTimelineRes?.data
        }));
      })
      .catch(() => {
        setIndustryBenchmarksData((prev: any) => ({
          ...prev,
          reductionData: undefined,
          reductionTimelineData: undefined
        }));
      })
      .finally(() => {
        setIsReductionLoading(false);
      });
  }, [refreshPage]);

  const handleRefreshClick = () => {
    setRefresing(true);
    emissionCalculation(selectedYear)
      .then(() => {
        const year = selectedYear;
        setRefreshPage((page) => {
          const next = !page;
          silentRefresh.current = {page: next, year};

          return next;
        });
        const companyId = useCompanyStore.getState().companyId;

        void regenerateOverviewInsights(companyId, year);
      })
      .catch(() => {})
      .finally(() => {
        setRefresing(false);
      });
  };

  return (
    <Flex className="metricsMainContainer">
      <Flex justify="space-between" align="center" style={{width: '100%'}}>
        <div style={{marginBottom: '10px'}}>
          <Select
            placeholder={'Select Year'}
            value={selectedYear}
            className="LAselect"
            onChange={(value) => {
              setSelectedYear(value);
              setFacilitySelectedYear(value);
            }}
            disabled={isLoading || refresing}
            // defaultActiveFirstOption={true}
            options={yearSelect()}
          />
        </div>
        {shouldBlockReadOnly() ? (
          <Button
            type="default"
            icon={<ReloadOutlined />}
            style={{marginBottom: '10px'}}
            title="Reflecting data from Carbon Accounting"
            loading={refresing}
            onClick={() => {
              usePlatformStore.getState().setOpenOnFreeTrialMessageModel(true);
            }}
          ></Button>
        ) : (
          <Button
            type="default"
            icon={<ReloadOutlined />}
            style={{marginBottom: '10px'}}
            title="Reflecting data from Carbon Accounting"
            loading={refresing}
            onClick={() => {
              handleRefreshClick();
            }}
          ></Button>
        )}
      </Flex>
      {isLoading ? (
        <Loader isHome={true} />
      ) : (
        <div>
          <ListOfActivities
            data={listData}
            industryBenchmarksData={industryBenchmarksData}
            selectedYear={selectedYear}
            activityData={activityData}
            refreshKey={refreshPage}
          />
        </div>
      )}
    </Flex>
  );
};

export default Metrics;
